import { Component, useState } from 'react'
import { Canvas } from '@react-three/fiber'

let webglSupported = null
function detectWebGL() {
  if (webglSupported !== null) return webglSupported
  try {
    const c = document.createElement('canvas')
    webglSupported = !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch (err) {
    webglSupported = false
  }
  return webglSupported
}

/** Catches renderer-creation failures (context limit / no GPU) so one
    canvas can never take the page down — it just renders nothing. */
class CanvasErrorBoundary extends Component {
  state = { err: false }

  static getDerivedStateFromError() {
    return { err: true }
  }

  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && this.state.err) {
      this.setState({ err: false })
    }
  }

  componentDidCatch() {
    this.props.onError?.()
  }

  render() {
    return this.state.err ? null : this.props.children
  }
}

/**
 * Canvas that (1) never mounts when WebGL is unavailable, (2) survives
 * context loss by remounting, (3) degrades to nothing if a renderer can't
 * even be created — an unhandled rejection here is what crashed the page.
 */
export default function SafeCanvas({ canvasProps, children }) {
  const [supported] = useState(detectWebGL)
  const [ctxKey, setCtxKey] = useState(0)
  const [failed, setFailed] = useState(false)

  if (!supported || failed) return null

  return (
    <CanvasErrorBoundary resetKey={ctxKey} onError={() => setFailed(true)}>
      <Canvas
        key={ctxKey}
        onCreated={(state) => {
          state.gl.domElement.addEventListener(
            'webglcontextlost',
            (e) => {
              e.preventDefault()
              setTimeout(() => setCtxKey((k) => k + 1), 120)
            },
            false
          )
        }}
        {...canvasProps}
      >
        {children}
      </Canvas>
    </CanvasErrorBoundary>
  )
}
